import { motion } from "framer-motion";
import { Check } from "lucide-react";
import agntLogoSrc from "assets/landing/agnt-logo-nobg.png";
import {
  AnimatedSection,
  AnimatedGradientBorder,
  GlassGlow,
  NoiseTexture,
} from "../shared";

const FEATURES = [
  "Crypto-linked debit card for everyday spending",
  "Frictionless fiat on/off-ramp built into the hub",
  "Agent-linked settlements — agents earn, you spend",
  "Unified balance overview across all wallets",
  "Full multi-chain support out of the box",
];

export default function NeobankCards() {
  return (
    <section
      id="neobank"
      className="py-12 md:py-16 px-6 relative overflow-hidden bg-[#f5f5f5]"
    >
      <div className="max-w-7xl max-lg:max-w-[700px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
        <AnimatedSection>
          <div>
            <span className="inline-flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-black/40 mb-5 before:content-[''] before:w-5 before:h-px before:bg-gradient-to-r before:from-black/30 before:to-transparent">
              Financial Layer
            </span>
            <h2 className="font-space-grotesk text-4xl md:text-6xl font-bold tracking-[-0.03em] leading-[1.1] text-black">
              Neo3 Bank & Cards
            </h2>
            <p className="text-lg text-black/50 mt-5 mb-10 leading-relaxed">
              Your crypto finances, unified. Connect your agents directly to a
              spendable balance.
            </p>
            <ul className="flex flex-col gap-5">
              {FEATURES.map((feature, i) => (
                <motion.li
                  key={i}
                  className="flex items-center gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.08 * i }}
                >
                  <motion.div
                    className="w-6 h-6 rounded-full bg-black/10 flex items-center justify-center shrink-0"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + 0.08 * i, type: "spring" }}
                  >
                    <Check className="w-3.5 h-3.5 text-black/70" />
                  </motion.div>
                  <span className="text-base text-black/55 font-medium">
                    {feature}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <AnimatedGradientBorder className="rounded-3xl">
            <div className="relative h-[340px] md:h-[520px] bg-gradient-to-b from-[#151518] to-[#111] rounded-3xl p-4  md:p-8 flex flex-col justify-end overflow-hidden">
              <GlassGlow intensity={0.12} size={65} />
              <NoiseTexture />

              <div className="absolute md:top-8 top-4 md:left-8 left-4 z-10">
                <div className="text-sm font-medium text-white/50 mb-1">
                  Total Balance
                </div>
                <motion.div
                  className="font-space-grotesk text-4xl md:text-5xl font-bold tracking-tight text-white"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1 }}
                >
                  $24,892.50
                </motion.div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-emerald-400/80 text-sm font-bold flex items-center gap-1">
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      className="w-3.5 h-3.5"
                    >
                      <path d="M2 12L6 7L9 9L14 3" />
                      <path d="M11 3H14V6" />
                    </svg>
                    +12.4%
                  </span>
                  <span className="text-white/40 text-xs">past 7d</span>
                </div>
              </div>

              <motion.div
                className="w-full rounded-2xl relative overflow-hidden z-10 md:max-h-[320px] md:max-w-[500px] lg:max-w-full lg:max-h-full"
                style={{ aspectRatio: "85.6 / 53.98" }}
                whileHover={{ y: -4, rotateY: 2, rotateX: -1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background:
                      "linear-gradient(145deg, #222228 0%, #1a1a20 30%, #141418 60%, #111115 100%)",
                  }}
                />
                <div className="absolute inset-0 rounded-2xl border border-white/[0.1]" />
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background:
                      "linear-gradient(125deg, rgba(255,255,255,0.07) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.02) 100%)",
                  }}
                />
                <div
                  className="absolute top-0 left-0 w-full h-[1px]"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
                  }}
                />
                <img
                  src={agntLogoSrc}
                  alt=""
                  className="absolute -right-[5%] top-[10%] h-[85%] w-[50%] object-contain opacity-[0.06] pointer-events-none select-none"
                />

                <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <span className="font-space-grotesk font-bold text-base tracking-[0.18em] text-white/90 uppercase">
                      AGNT Neo3
                    </span>
                    <svg
                      viewBox="0 0 48 30"
                      className="w-10 h-auto opacity-90"
                      fill="none"
                    >
                      <circle
                        cx="18"
                        cy="15"
                        r="12"
                        fill="#EB001B"
                        opacity="0.85"
                      />
                      <circle
                        cx="30"
                        cy="15"
                        r="12"
                        fill="#F79E1B"
                        opacity="0.85"
                      />
                      <path
                        d="M24 5.4a12 12 0 010 19.2 12 12 0 000-19.2z"
                        fill="#FF5F00"
                        opacity="0.9"
                      />
                    </svg>
                  </div>

                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="w-10 h-7 rounded-md overflow-hidden relative">
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #c9a84c 0%, #f0d882 25%, #c9a84c 50%, #a08030 75%, #c9a84c 100%)",
                        }}
                        className="absolute inset-0"
                      />
                      <div className="absolute inset-[2px] rounded-sm grid grid-cols-3 grid-rows-3 gap-px opacity-40">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <div
                            key={i}
                            className="bg-black/20 rounded-[0.5px]"
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="font-mono text-[15px] tracking-[0.3em] text-white/60">
                      4539 •••• •••• 4092
                    </div>
                    <div className="flex justify-between items-end">
                      <div className="flex gap-8">
                        <div>
                          <div className="text-[7px] uppercase tracking-[0.15em] text-white/25 mb-0.5">
                            Card Holder
                          </div>
                          <div className="font-space-grotesk text-xs tracking-[0.08em] text-white/60 uppercase">
                            AGNT User
                          </div>
                        </div>
                        <div>
                          <div className="text-[7px] uppercase tracking-[0.15em] text-white/25 mb-0.5">
                            Expires
                          </div>
                          <div className="font-space-grotesk text-xs tracking-[0.08em] text-white/60">
                            12/28
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </AnimatedGradientBorder>
        </AnimatedSection>
      </div>
    </section>
  );
}
